/**
 * Filter controls shared by every admin list.
 *
 * Design rules, learned the hard way from the first pass:
 *  - One row, wrapping. Anything that forces a single line overflows the page
 *    at 1280px, never mind on a tablet.
 *  - Every control is the same height as `clay-select` (h-12, border-2). Mixing
 *    h-10 inputs with h-12 selects is what made the bar look broken.
 *  - The date range is ONE control in the common case. Two date inputs plus
 *    three preset buttons plus a clear is six widgets to express "last 30 days";
 *    a select says it in one, and reveals the inputs only for a custom range.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '@/lib/api';

/** Label + control, so every field in the bar lines up. */
export function FilterField({
  label,
  htmlFor,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {/* Matches the label style the list pages already use — uppercase here
          made the two halves of the same bar look like different designs. */}
      <label className="text-xs font-semibold text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

/** The filter row itself. Wraps rather than overflowing. */
export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-end gap-3">{children}</div>;
}

/** Matches `clay-select` so text inputs sit level with the dropdowns. */
const FIELD_CLASS =
  'h-12 rounded-input border-2 border-input bg-card px-4 text-base text-foreground transition-all placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15';

const DAY_MS = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Which preset (if any) the current from/to corresponds to. */
function preset_of(from: string, to: string): string {
  if (!from && !to) return '';
  const today = iso(new Date());
  if (to !== today) return 'custom';
  for (const days of [7, 30, 90]) {
    if (from === iso(new Date(Date.now() - (days - 1) * DAY_MS))) return String(days);
  }
  return 'custom';
}

export type DateRange = { from: string; to: string };

/**
 * Date range as a single select, with the two inputs revealed only when the
 * admin actually wants a custom window.
 */
export function DateRangeFilter({
  from,
  to,
  on_change,
  label = 'Date',
}: {
  from: string;
  to: string;
  on_change: (next: DateRange) => void;
  label?: string;
}) {
  const current = preset_of(from, to);
  const [custom_open, set_custom_open] = useState(current === 'custom');

  useEffect(() => {
    if (current === 'custom') set_custom_open(true);
  }, [current]);

  function choose(value: string) {
    if (value === '') {
      set_custom_open(false);
      return on_change({ from: '', to: '' });
    }
    if (value === 'custom') {
      set_custom_open(true);
      return;
    }
    set_custom_open(false);
    const days = Number(value);
    on_change({ from: iso(new Date(Date.now() - (days - 1) * DAY_MS)), to: iso(new Date()) });
  }

  return (
    <>
      <FilterField label={label} htmlFor="filter-date-range">
        <select
          id="filter-date-range"
          data-testid="filter-date-range"
          className="clay-select"
          value={custom_open && current !== '' ? 'custom' : current}
          onChange={(e) => choose(e.target.value)}
        >
          <option value="">Any time</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="custom">Custom range…</option>
        </select>
      </FilterField>

      {custom_open && (
        <>
          <FilterField label="From" htmlFor="filter-from">
            <input
              id="filter-from"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => on_change({ from: e.target.value, to })}
              className={`${FIELD_CLASS} w-44`}
              data-testid="filter-from"
            />
          </FilterField>
          <FilterField label="To" htmlFor="filter-to">
            <input
              id="filter-to"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => on_change({ from, to: e.target.value })}
              className={`${FIELD_CLASS} w-44`}
              data-testid="filter-to"
            />
          </FilterField>
        </>
      )}
    </>
  );
}

type VendorOption = { id: string; business_name: string | null; city: string | null };

/**
 * Pick one vendor to narrow a list to.
 *
 * A plain dropdown was never viable — there are ~90 vendors and it only grows —
 * so this searches by name as you type. Once chosen it shows the vendor's NAME,
 * because the id in the URL tells an admin nothing.
 */
export function VendorFilter({
  vendor_id,
  on_change,
}: {
  vendor_id: string;
  on_change: (id: string) => void;
}) {
  const [term, set_term] = useState('');
  const [open, set_open] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  const selected = useQuery({
    queryKey: ['vendor-filter-selected', vendor_id],
    queryFn: async () =>
      (await api.get<{ business_name: string | null }>(`/admin/vendors/${vendor_id}`)).data,
    enabled: vendor_id !== '',
  });

  const results = useQuery({
    queryKey: ['vendor-filter-search', term],
    queryFn: async () => {
      const res = await api.get<{ items: VendorOption[] }>('/admin/vendors', {
        params: { q: term, page_size: 10 },
      });
      return res.data.items;
    },
    enabled: open && term.trim().length >= 2,
  });

  useEffect(() => {
    if (!open) return;
    const on_click = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) set_open(false);
    };
    document.addEventListener('mousedown', on_click);
    return () => document.removeEventListener('mousedown', on_click);
  }, [open]);

  if (vendor_id) {
    return (
      <FilterField label="Vendor">
        <div className={`${FIELD_CLASS} flex w-56 items-center justify-between gap-2`}>
          <span className="truncate text-sm" data-testid="vendor-filter-active">
            {selected.data?.business_name ?? 'Loading…'}
          </span>
          <button
            type="button"
            className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => on_change('')}
            data-testid="vendor-filter-clear"
            aria-label="Clear vendor filter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </FilterField>
    );
  }

  return (
    // The ref must wrap the WHOLE control, not the dropdown: the list only
    // exists once two characters are typed, so a ref on it is null beforehand
    // and the outside-click handler treated a click on the input itself as
    // "outside", closing the list the instant it opened.
    <div ref={wrapper} className="relative">
      <FilterField label="Vendor" htmlFor="vendor-filter">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id="vendor-filter"
          placeholder="Search by name…"
          value={term}
          onFocus={() => set_open(true)}
          onChange={(e) => {
            set_term(e.target.value);
            set_open(true);
          }}
          className={`${FIELD_CLASS} w-56 pl-10`}
          data-testid="vendor-filter-input"
        />
      </div>
      {open && term.trim().length >= 2 && (
        <ul
          className="absolute top-full z-30 mt-1 max-h-64 w-72 overflow-y-auto rounded-input border-2 border-border bg-card py-1 shadow-l2"
          data-testid="vendor-filter-results"
        >
          {results.isLoading ? (
            <li className="px-4 py-2 text-sm text-muted-foreground">Searching…</li>
          ) : !results.data || results.data.length === 0 ? (
            <li className="px-4 py-2 text-sm text-muted-foreground">No vendors match “{term}”.</li>
          ) : (
            results.data.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-4 py-2 text-left hover:bg-muted"
                  // onMouseDown, not onClick: the outside-click handler fires
                  // first and would close the list before a click landed.
                  onMouseDown={() => {
                    on_change(v.id);
                    set_open(false);
                    set_term('');
                  }}
                  data-testid={`vendor-option-${v.id}`}
                >
                  <span className="text-sm font-medium">{v.business_name ?? v.id}</span>
                  {v.city && <span className="text-xs text-muted-foreground">{v.city}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      </FilterField>
    </div>
  );
}
